import {
  type ClusterBubbles,
  type ClusterMember,
  type ClusterRunStatus,
  entityColorVar,
  GRAPH_MAX_SEEDS,
  type SchemaSnapshot,
  semanticVar,
} from '@hoopoe/shared';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { type HierarchyCircularNode, hierarchy, pack } from 'd3-hierarchy';
import { Loader2, Lock, Network, Orbit, Pencil, RefreshCw } from 'lucide-react';
import { type KeyboardEvent, useId, useMemo, useState } from 'react';
import { ColorLegend } from '../components/colors/color-by';
import { DataNumber, DataText } from '../components/data/data-text';
import { EntityLink } from '../components/entities/entity-link';
import { Section } from '../components/graph/panels';
import { EmptyState } from '../components/guidance/empty-state';
import { FieldHelp } from '../components/guidance/field-help';
import { Tip } from '../components/guidance/tip';
import { useScreenTour } from '../components/guidance/tour';
import { PageHeader } from '../components/layout/app-shell';
import { Swatch, slotName } from '../components/schema/color-slot-picker';
import { Button } from '../components/ui/button';
import { ApiError } from '../lib/api';
import { clipWords } from '../lib/clip-words';
import { useCluster, useClusters, useRecomputeClusters, useRenameCluster } from '../lib/clusters';
import { useColorMode, useStyleOf } from '../lib/colors';
import { useSchema } from '../lib/schema';
import { useCan } from '../lib/session';

// Bubble / cluster view (PLAN §9 "Bubble / clusters", §12.7 screen 7; task 34): the communities the cluster job found
// in the relation graph, each drawn as a circle of its members. A member's bubble is sized by its centrality inside
// the cluster (links, or how much it bridges the others); its fill follows the active "Color by" mode and the
// cluster's outline its cluster color. The selected cluster lives in the URL (?cluster=id).

export interface ClustersSearch {
  cluster?: string;
}

export const Route = createFileRoute('/clusters')({
  validateSearch: (s: Record<string, unknown>): ClustersSearch =>
    typeof s.cluster === 'string' && /^[0-9A-Za-z]{1,40}$/.test(s.cluster) ? { cluster: s.cluster } : {},
  component: ClustersPage,
});

const TOUR = {
  id: 'clusters',
  steps: [
    {
      target: '[data-tour="clusters-chart"]',
      title: 'Clusters at a glance',
      body: 'Each circle is a cluster: entities more closely linked to each other than to the rest. Bigger bubbles are more central.',
      learnMore: 'clusters',
    },
    {
      target: '[data-tour="clusters-size"]',
      title: 'Size by',
      body: 'Links sizes members by how many links they have inside the cluster; Bridging by how much they connect the others.',
    },
    {
      target: '[data-tour="clusters-panel"]',
      title: 'Open a cluster',
      body: 'Pick a cluster to see its members, rename it, or open it in the graph.',
      learnMore: 'clusters',
    },
  ],
};

type SizeBy = 'links' | 'bridging';
const SIZE_LABELS: Record<SizeBy, string> = { links: 'Links', bridging: 'Bridging' };
const VIEW = 720;

type Leaf = { member?: ClusterMember; clusterId?: string; children?: Leaf[]; value?: number };

const sizeOf = (m: ClusterMember, by: SizeBy) => (by === 'links' ? Math.max(0.5, m.degree) : 0.05 + (m.betweenness ?? 0));

function ago(ms: number) {
  const s = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (s < 90) return 'just now';
  const m = Math.round(s / 60);
  if (m < 90) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 36) return `${h} h ago`;
  return new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function RunStatus({ lastRun, running }: { lastRun: ClusterRunStatus | null; running: ClusterRunStatus | null }) {
  if (running)
    return (
      <p className="flex items-center gap-1.5 text-sm text-muted-foreground" role="status" data-testid="clusters-status">
        <Loader2 className="size-4 animate-spin" aria-hidden="true" /> Recomputing clusters… they update here when it is done.
      </p>
    );
  if (!lastRun) return null;
  return (
    <p className="text-sm text-muted-foreground" role="status" data-testid="clusters-status">
      {lastRun.state === 'failed' ? 'The last recompute failed' : 'Computed'} {ago(lastRun.finishedAt ?? lastRun.createdAt)}
      {lastRun.note ? ` · ${lastRun.note}` : ''}
    </p>
  );
}

function ClustersPage() {
  useScreenTour(TOUR);
  const { data: snap } = useSchema();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: '/clusters' });
  const q = useClusters();
  const recompute = useRecomputeClusters();
  const canReview = useCan('content.review');
  const [sizeBy, setSizeBy] = useState<SizeBy>('links');
  const sizeName = useId();
  const select = (id: string | null) => navigate({ search: id ? { cluster: id } : {}, replace: true });

  const data = q.data;
  const recomputeButton = canReview ? (
    <Button variant="outline" size="sm" onClick={() => recompute.mutate()} disabled={recompute.isPending || !!data?.running}>
      <RefreshCw aria-hidden="true" /> Recompute
    </Button>
  ) : null;

  return (
    <>
      <PageHeader
        title="Clusters"
        description="Groups of entities more closely linked to each other than to the rest of the graph. Found every night and after large reviews."
        actions={recomputeButton}
      />
      {recompute.isError && (
        <p role="alert" className="mb-3 text-sm text-danger-foreground">
          {recompute.error instanceof ApiError ? recompute.error.message : 'The recompute could not be started.'}
        </p>
      )}
      {!snap || q.isPending ? (
        <div className="h-96 animate-pulse rounded-lg bg-muted" aria-busy="true">
          <span className="sr-only">Loading…</span>
        </div>
      ) : q.isError || !data ? (
        <p role="alert" className="text-sm text-danger-foreground">
          The clusters could not be loaded. Reload the page to try again.
        </p>
      ) : data.clusters.length === 0 ? (
        <>
          <RunStatus lastRun={data.lastRun} running={data.running} />
          <EmptyState icon={Orbit} title="No clusters yet" learnMore="clusters" action={recomputeButton}>
            Clusters appear once entities are linked to each other (three or more closely linked entities make a cluster).
            {canReview ? ' Recompute them now, or wait for tonight’s run.' : ' They are recomputed every night.'}
          </EmptyState>
        </>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <RunStatus lastRun={data.lastRun} running={data.running} />
            <fieldset className="ms-auto flex items-center gap-1.5 text-sm" data-tour="clusters-size">
              <legend className="sr-only">Size bubbles by</legend>
              <span aria-hidden="true" className="text-muted-foreground">
                Size by
              </span>
              <div className="flex rounded-md border border-input bg-surface p-0.5">
                {(Object.keys(SIZE_LABELS) as SizeBy[]).map(k => (
                  <label
                    key={k}
                    className="cursor-pointer rounded px-2 py-0.5 text-muted-foreground has-[:checked]:bg-muted has-[:checked]:font-medium has-[:checked]:text-foreground has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring"
                  >
                    <input
                      type="radio"
                      name={sizeName}
                      value={k}
                      className="sr-only"
                      checked={sizeBy === k}
                      onChange={() => setSizeBy(k)}
                    />
                    {SIZE_LABELS[k]}
                  </label>
                ))}
              </div>
              <FieldHelp
                label="Size by"
                help="Links: how strongly a member is linked inside its cluster (reviewed links count more). Bridging: how often it sits on the shortest path between two other members (betweenness)."
                learnMore="clusters"
              />
            </fieldset>
          </div>
          <Tip id="clusters" learnMore="clusters">
            Switch “Color by” to Cluster to see these colors on every screen: lists, profiles, the graph, the map and the timeline.
          </Tip>
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_26rem]">
            <BubbleChart data={data} snap={snap} sizeBy={sizeBy} selected={search.cluster ?? null} onSelect={select} />
            <div data-tour="clusters-panel" className="space-y-3">
              {search.cluster ? (
                <ClusterPanel id={search.cluster} snap={snap} canReview={canReview} onClose={() => select(null)} />
              ) : (
                <ClusterList data={data} onSelect={select} />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function BubbleChart({
  data,
  snap,
  sizeBy,
  selected,
  onSelect,
}: {
  data: ClusterBubbles;
  snap: SchemaSnapshot;
  sizeBy: SizeBy;
  selected: string | null;
  onSelect: (id: string) => void;
}) {
  const styleOf = useStyleOf(snap);
  const { mode } = useColorMode();
  const root = useMemo(() => {
    const tree: Leaf = {
      children: data.clusters.map(c => ({
        clusterId: c.id,
        children: c.members.map(m => ({ member: m, value: sizeOf(m, sizeBy) })),
      })),
    };
    const h = hierarchy<Leaf>(tree)
      .sum(d => d.value ?? 0)
      .sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
    return pack<Leaf>()
      .size([VIEW, VIEW])
      .padding(d => (d.depth === 0 ? 10 : 3))(h);
  }, [data, sizeBy]);
  const clusterNodes = (root.children ?? []) as HierarchyCircularNode<Leaf>[];
  const byId = new Map(data.clusters.map(c => [c.id, c]));
  const legend = useMemo(() => data.clusters.flatMap(c => c.members.map(m => styleOf(m))), [data, styleOf]);
  const onKey = (e: KeyboardEvent, id: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(id);
    }
  };
  const members = data.clusters.reduce((n, c) => n + c.members.length, 0);
  return (
    <div className="space-y-2">
      <div className="rounded-lg border bg-surface p-2" data-tour="clusters-chart">
        <svg
          viewBox={`0 0 ${VIEW} ${VIEW}`}
          className="mx-auto block h-auto w-full max-w-[720px]"
          aria-label={`Bubble chart: ${data.clusters.length} ${data.clusters.length === 1 ? 'cluster' : 'clusters'}, ${members} members shown`}
          data-testid="cluster-bubbles"
        >
          {clusterNodes.map(node => {
            const c = byId.get(node.data.clusterId ?? '');
            if (!c) return null;
            const isSel = selected === c.id;
            const label = `${c.label ?? 'Unnamed cluster'}, ${c.size} ${c.size === 1 ? 'member' : 'members'}, ${slotName(c.colorSlot)}`;
            return (
              // biome-ignore lint/a11y/useSemanticElements: an SVG group can't be a <button>; it gets the role and keys instead
              <g
                key={c.id}
                role="button"
                tabIndex={0}
                aria-label={label}
                aria-pressed={isSel}
                data-cluster-id={c.id}
                data-cluster-slot={c.colorSlot ?? 'none'}
                className="cursor-pointer outline-none focus-visible:[&>circle:first-child]:stroke-[5]"
                onClick={() => onSelect(c.id)}
                onKeyDown={e => onKey(e, c.id)}
              >
                <circle
                  cx={node.x}
                  cy={node.y}
                  r={node.r}
                  fill={entityColorVar(c.colorSlot)}
                  fillOpacity={isSel ? 0.22 : 0.1}
                  stroke={entityColorVar(c.colorSlot)}
                  strokeWidth={isSel ? 4 : 2}
                />
                {(node.children ?? []).map(leaf => {
                  const m = leaf.data.member;
                  if (!m) return null;
                  const st = styleOf(m);
                  return (
                    <circle
                      key={m.id}
                      cx={leaf.x}
                      cy={leaf.y}
                      r={Math.max(1.5, leaf.r)}
                      fill={entityColorVar(st.colorSlot)}
                      fillOpacity={st.fade ? 0.35 : 0.9}
                      stroke={semanticVar('surface')}
                      strokeWidth={1}
                      strokeDasharray={m.reviewStatus === 'unreviewed' ? '2 1.5' : undefined}
                      data-entity-id={m.id}
                    >
                      <title>{`${m.displayName} · ${Math.round(m.degree * 10) / 10} link strength${m.betweenness !== null ? ` · bridging ${Math.round(m.betweenness * 100)}%` : ''} · ${st.why}`}</title>
                    </circle>
                  );
                })}
                {node.r > 36 && (
                  <text
                    x={node.x}
                    y={node.y - node.r + 16}
                    textAnchor="middle"
                    className="pointer-events-none text-[13px] font-medium"
                    fill={semanticVar('foreground')}
                    stroke={semanticVar('surface')}
                    strokeWidth={3}
                    paintOrder="stroke"
                  >
                    {clipWords(c.label ?? 'Unnamed cluster', Math.max(6, Math.floor(node.r / 4)))}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>
      <div className="flex flex-wrap gap-x-8 gap-y-2 rounded-lg border bg-surface p-3">
        <div className="min-w-48 flex-1">
          <p className="mb-1 text-xs font-medium">Fill: Color by {mode === 'type' ? 'type' : mode === 'rules' ? 'rules' : 'cluster'}</p>
          <ColorLegend styles={legend} />
        </div>
        <p className="max-w-64 text-xs text-muted-foreground">
          Outline: the cluster’s color. Bubble size: {sizeBy === 'links' ? 'links inside the cluster' : 'bridging (betweenness)'}. Dashed:
          unreviewed.
          {data.more > 0 && ` ${data.more} smaller ${data.more === 1 ? 'cluster is' : 'clusters are'} not shown.`}
        </p>
      </div>
    </div>
  );
}

function ClusterList({ data, onSelect }: { data: ClusterBubbles; onSelect: (id: string) => void }) {
  return (
    <Section title={`Clusters (${data.clusters.length + data.more})`}>
      <ul className="max-h-[36rem] space-y-0.5 overflow-auto text-sm">
        {data.clusters.map(c => (
          <li key={c.id}>
            <button
              type="button"
              onClick={() => onSelect(c.id)}
              className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-start hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring"
              data-cluster-id={c.id}
            >
              <Swatch slot={c.colorSlot} />
              <span className="sr-only">{slotName(c.colorSlot)}</span>
              <DataText className="min-w-0 flex-1 truncate">{c.label ?? 'Unnamed cluster'}</DataText>
              {c.sensitivity === 'confidential' && <Lock className="size-3.5 text-muted-foreground" aria-label="Confidential" />}
              <DataNumber className="text-xs text-muted-foreground">{c.size}</DataNumber>
            </button>
          </li>
        ))}
      </ul>
    </Section>
  );
}

const SOURCE_TEXT = { ai: 'Named by AI', manual: 'Named by a reviewer', auto: 'Placeholder name (after its most central member)' };

function ClusterPanel({ id, snap, canReview, onClose }: { id: string; snap: SchemaSnapshot; canReview: boolean; onClose: () => void }) {
  const q = useCluster(id);
  const rename = useRenameCluster();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputId = useId();
  if (q.isPending)
    return (
      <div className="h-64 animate-pulse rounded-lg bg-muted" aria-busy="true">
        <span className="sr-only">Loading…</span>
      </div>
    );
  if (q.isError || !q.data)
    return (
      <Section
        title="Cluster"
        actions={
          <Button size="sm" variant="ghost" onClick={onClose}>
            All clusters
          </Button>
        }
      >
        <p role="alert" className="text-sm text-muted-foreground">
          This cluster no longer exists: clusters change when they are recomputed.
        </p>
      </Section>
    );
  const c = q.data;
  const focus = c.members.slice(0, GRAPH_MAX_SEEDS).map(m => m.id);
  const save = () =>
    rename.mutate(
      { id: c.id, label: draft },
      {
        onSuccess: () => setEditing(false),
      },
    );
  return (
    <Section
      title="Cluster"
      actions={
        <Button size="sm" variant="ghost" onClick={onClose}>
          All clusters
        </Button>
      }
    >
      <div className="space-y-3 text-sm" data-testid="cluster-panel">
        <div className="flex items-start gap-2">
          <Swatch slot={c.colorSlot} className="mt-1" />
          <div className="min-w-0 flex-1">
            <p className="font-semibold">
              <DataText>{c.label ?? 'Unnamed cluster'}</DataText>
              {c.sensitivity === 'confidential' && <Lock className="ms-1 inline size-3.5" aria-label="Confidential" />}
            </p>
            <p className="text-xs text-muted-foreground">
              {SOURCE_TEXT[c.labelSource]} · {c.size} {c.size === 1 ? 'member' : 'members'} · {slotName(c.colorSlot)}
            </p>
          </div>
          {canReview && !editing && (
            <Button
              size="icon"
              variant="ghost"
              aria-label="Rename cluster"
              onClick={() => {
                setDraft(c.label ?? '');
                setEditing(true);
              }}
            >
              <Pencil aria-hidden="true" />
            </Button>
          )}
        </div>
        {editing && (
          <form
            className="space-y-1.5"
            onSubmit={e => {
              e.preventDefault();
              save();
            }}
          >
            <label htmlFor={inputId} className="text-xs font-medium">
              Cluster name
            </label>
            <input
              id={inputId}
              dir="auto"
              value={draft}
              maxLength={80}
              onChange={e => setDraft(e.target.value)}
              placeholder="e.g. Harbor Aid donors"
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
            />
            <p className="text-xs text-muted-foreground">
              Your name is kept when the clusters are recomputed, as long as this cluster continues.
            </p>
            {rename.isError && (
              <p role="alert" className="text-xs text-danger-foreground">
                {rename.error instanceof ApiError ? rename.error.message : 'The name could not be saved.'}
              </p>
            )}
            <div className="flex gap-2">
              <Button size="sm" type="submit" disabled={rename.isPending}>
                Save name
              </Button>
              <Button size="sm" type="button" variant="ghost" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </div>
          </form>
        )}
        <Link
          to="/graph"
          search={{ focus: focus.join(',') }}
          className="inline-flex items-center gap-1 text-sm font-medium underline underline-offset-2"
        >
          <Network className="size-4" aria-hidden="true" /> Open in graph
        </Link>
        <table className="w-full text-sm">
          <caption className="sr-only">Members, most central first</caption>
          <thead>
            <tr className="text-xs text-muted-foreground">
              <th scope="col" className="py-1 text-start font-medium">
                Member
              </th>
              <th scope="col" className="py-1 text-end font-medium">
                Links
              </th>
              <th scope="col" className="py-1 text-end font-medium">
                Bridging
              </th>
            </tr>
          </thead>
          <tbody>
            {c.members.map(m => (
              <tr key={m.id} className="border-t" data-member-id={m.id}>
                <td className="max-w-0 py-1 pe-2">
                  <EntityLink schema={snap} entity={m} className="w-full" />
                </td>
                <td className="py-1 text-end">
                  <DataNumber>{Math.round(m.degree * 10) / 10}</DataNumber>
                </td>
                <td className="py-1 text-end">
                  <DataNumber>{m.betweenness === null ? '–' : `${Math.round(m.betweenness * 100)}%`}</DataNumber>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}
